import React, { useState, useEffect, useCallback } from 'react';
import {
  getEmailForwardingStatus,
  generateEmailForwardingAddress,
  regenerateEmailForwardingAddress,
  getEmailForwardingHistory,
} from '../services/settingsApi';

function EmailForwardingPage() {
  const [address, setAddress] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [copied, setCopied] = useState(false);

  const [emails, setEmails] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyOffset, setHistoryOffset] = useState(0);
  const HISTORY_LIMIT = 20;

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      setStatusLoading(true);
      const res = await getEmailForwardingStatus();
      if (res.enabled) {
        setAddress(res.address);
        await loadHistory(0);
      }
    } catch (err) {
      console.error('Error loading email forwarding status:', err);
    } finally {
      setStatusLoading(false);
    }
  };

  const loadHistory = useCallback(async (offset = 0, append = false) => {
    try {
      setHistoryLoading(true);
      const res = await getEmailForwardingHistory(HISTORY_LIMIT, offset);
      if (append) {
        setEmails(prev => [...prev, ...(res.emails || [])]);
      } else {
        setEmails(res.emails || []);
      }
      setHistoryTotal(res.total || 0);
      setHistoryOffset(offset);
    } catch (err) {
      console.error('Error loading forwarding history:', err);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const handleGenerate = async () => {
    try {
      setGenerating(true);
      setError(null);
      const res = await generateEmailForwardingAddress();
      setAddress(res.address);
      setSuccess('Forwarding address created!');
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleRegenerate = async () => {
    if (!window.confirm('Are you sure? Your old forwarding address will stop working immediately.')) {
      return;
    }
    try {
      setRegenerating(true);
      setError(null);
      const res = await regenerateEmailForwardingAddress();
      setAddress(res.address);
      setSuccess('Forwarding address regenerated. Old address is now invalid.');
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setError(err.message);
    } finally {
      setRegenerating(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = address;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleLoadMore = () => {
    const newOffset = historyOffset + HISTORY_LIMIT;
    loadHistory(newOffset, true);
  };

  const handleRefresh = () => {
    loadHistory(0, false);
  };

  const formatTime = (dateStr) => {
    const d = new Date(dateStr);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = d.toDateString() === yesterday.toDateString();

    const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (isToday) return `Today ${time}`;
    if (isYesterday) return `Yesterday ${time}`;
    return `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${time}`;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'success':
        return { label: 'Success', bg: 'var(--color-success-light, #d1fae5)', color: 'var(--color-success, #10b981)' };
      case 'processing':
        return { label: 'Processing', bg: '#fef3c7', color: '#d97706' };
      case 'failed':
        return { label: 'Failed', bg: 'var(--color-error-light, #fee2e2)', color: 'var(--color-error, #ef4444)' };
      case 'verification':
        return { label: 'Verified', bg: '#dbeafe', color: '#2563eb' };
      default:
        return { label: status, bg: '#F5F5F5', color: '#71717A' };
    }
  };

  const styles = {
    container: {
      padding: 'var(--space-6)',
      maxWidth: '800px',
      margin: '0 auto',
    },
    title: {
      fontSize: 'var(--font-size-2xl)',
      fontWeight: 'var(--font-weight-bold)',
      marginBottom: 'var(--space-6)',
      color: 'var(--color-text-primary)',
      letterSpacing: '-0.025em',
    },
    section: {
      backgroundColor: 'var(--color-bg-base)',
      borderRadius: 'var(--radius-lg)',
      padding: 'var(--space-6)',
      marginBottom: 'var(--space-6)',
      boxShadow: 'var(--shadow-sm)',
      border: '1px solid var(--color-border-light)',
    },
    sectionTitle: {
      fontSize: 'var(--font-size-lg)',
      fontWeight: 'var(--font-weight-semibold)',
      marginBottom: 'var(--space-4)',
      color: 'var(--color-text-primary)',
    },
    errorBox: {
      padding: 'var(--space-3)',
      backgroundColor: 'var(--color-error-light)',
      border: '1px solid var(--color-error)',
      borderRadius: 'var(--radius-default)',
      marginBottom: 'var(--space-4)',
      fontSize: 'var(--font-size-sm)',
      color: 'var(--color-error-dark)',
    },
    successBox: {
      padding: 'var(--space-3)',
      backgroundColor: 'var(--color-success-light)',
      border: '1px solid var(--color-success)',
      borderRadius: 'var(--radius-default)',
      marginBottom: 'var(--space-4)',
      fontSize: 'var(--font-size-sm)',
      color: 'var(--color-success)',
    },
    primaryBtn: {
      padding: 'var(--space-2) var(--space-4)',
      borderRadius: 'var(--radius-default)',
      border: 'none',
      backgroundColor: 'var(--color-primary)',
      color: 'var(--color-text-inverse)',
      fontSize: 'var(--font-size-sm)',
      fontWeight: 'var(--font-weight-semibold)',
      cursor: 'pointer',
      transition: 'var(--transition-normal)',
    },
    secondaryBtn: {
      padding: 'var(--space-2) var(--space-4)',
      borderRadius: 'var(--radius-default)',
      border: '1px solid var(--color-border-default)',
      backgroundColor: 'var(--color-bg-base)',
      color: 'var(--color-text-primary)',
      fontSize: 'var(--font-size-sm)',
      fontWeight: 'var(--font-weight-semibold)',
      cursor: 'pointer',
      transition: 'var(--transition-normal)',
    },
    addressBox: {
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--space-2)',
      padding: 'var(--space-3)',
      backgroundColor: '#f0f9ff',
      border: '1px solid #bae6fd',
      borderRadius: 'var(--radius-default)',
      marginBottom: 'var(--space-4)',
    },
    addressText: {
      flex: 1,
      fontSize: 'var(--font-size-sm)',
      fontFamily: 'monospace',
      color: 'var(--color-text-primary)',
      wordBreak: 'break-all',
    },
    copyBtn: {
      padding: '6px 12px',
      borderRadius: 'var(--radius-default)',
      border: '1px solid var(--color-border-default)',
      backgroundColor: 'var(--color-bg-base)',
      fontSize: 'var(--font-size-xs)',
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      fontWeight: 'var(--font-weight-semibold)',
    },
    historyItem: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      padding: 'var(--space-3)',
      borderBottom: '1px solid var(--color-border-light)',
      gap: 'var(--space-3)',
    },
    historySubject: {
      fontSize: 'var(--font-size-sm)',
      fontWeight: 'var(--font-weight-semibold)',
      color: 'var(--color-text-primary)',
      marginBottom: '2px',
    },
    historyFrom: {
      fontSize: 'var(--font-size-xs)',
      color: 'var(--color-text-secondary)',
      marginBottom: '2px',
    },
    historyTime: {
      fontSize: 'var(--font-size-xs)',
      color: 'var(--color-text-tertiary)',
    },
    statusBadge: {
      fontSize: 'var(--font-size-xs)',
      fontWeight: 'var(--font-weight-semibold)',
      padding: '2px 8px',
      borderRadius: '12px',
      whiteSpace: 'nowrap',
    },
    expenseAmount: {
      fontSize: 'var(--font-size-sm)',
      fontWeight: 'var(--font-weight-semibold)',
      color: 'var(--color-text-primary)',
      whiteSpace: 'nowrap',
    },
    chatHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 'var(--space-3)',
    },
    refreshBtn: {
      padding: '4px 12px',
      borderRadius: 'var(--radius-default)',
      border: '1px solid var(--color-border-default)',
      backgroundColor: 'var(--color-bg-base)',
      color: 'var(--color-text-secondary)',
      fontSize: '12px',
      cursor: 'pointer',
    },
    loadMoreBtn: {
      display: 'block',
      margin: 'var(--space-3) auto 0',
      padding: '6px 16px',
      borderRadius: '16px',
      border: 'none',
      backgroundColor: 'rgba(0,0,0,0.06)',
      color: '#71717A',
      fontSize: '12px',
      cursor: 'pointer',
    },
    tipItem: {
      fontSize: 'var(--font-size-sm)',
      color: 'var(--color-text-secondary)',
      lineHeight: '1.6',
      marginBottom: 'var(--space-2)',
      paddingLeft: 'var(--space-4)',
    },
  };

  if (statusLoading) {
    return (
      <div style={styles.container}>
        <h2 style={styles.title}>Email Forward</h2>
        <div style={styles.section}>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Email Forward</h2>

      {/* Forwarding Address Section */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Your Forwarding Address</h3>

        {address ? (
          <>
            <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--space-3)', lineHeight: '1.5' }}>
              Forward any receipt or purchase confirmation email to this address. AI will automatically extract the expense details.
            </p>
            <div style={styles.addressBox}>
              <span style={styles.addressText}>{address}</span>
              <button
                onClick={handleCopy}
                style={{
                  ...styles.copyBtn,
                  backgroundColor: copied ? 'var(--color-success-light, #d1fae5)' : 'var(--color-bg-base)',
                  color: copied ? 'var(--color-success, #10b981)' : 'var(--color-text-primary)',
                }}
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <button
              onClick={handleRegenerate}
              disabled={regenerating}
              style={{
                ...styles.secondaryBtn,
                cursor: regenerating ? 'not-allowed' : 'pointer',
                opacity: regenerating ? 0.6 : 1,
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-secondary)',
              }}
            >
              {regenerating ? 'Regenerating...' : 'Regenerate Address'}
            </button>
          </>
        ) : (
          <>
            <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--space-4)', lineHeight: '1.5' }}>
              Generate a unique email address to start forwarding receipts and purchase confirmations. AI will automatically parse them into expenses.
            </p>
            <button
              onClick={handleGenerate}
              disabled={generating}
              style={{
                ...styles.primaryBtn,
                cursor: generating ? 'not-allowed' : 'pointer',
                opacity: generating ? 0.6 : 1,
              }}
              onMouseEnter={(e) => {
                if (!generating) e.target.style.backgroundColor = 'var(--color-primary-hover)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'var(--color-primary)';
              }}
            >
              {generating ? 'Generating...' : 'Generate Forwarding Address'}
            </button>
          </>
        )}

        {error && <div style={{ ...styles.errorBox, marginTop: 'var(--space-3)' }}>{error}</div>}
        {success && <div style={{ ...styles.successBox, marginTop: 'var(--space-3)' }}>{success}</div>}
      </div>

      {/* Instructions Section */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>How It Works</h3>
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <p style={styles.tipItem}>1. Forward any receipt or order confirmation email to your unique address above.</p>
          <p style={styles.tipItem}>2. AI reads the email and extracts the merchant name, date, amount, and category.</p>
          <p style={styles.tipItem}>3. A new expense is automatically created in your account.</p>
        </div>
        <div style={{
          padding: 'var(--space-3)',
          backgroundColor: '#fffbeb',
          border: '1px solid #fde68a',
          borderRadius: 'var(--radius-default)',
          fontSize: 'var(--font-size-sm)',
          color: '#92400e',
          lineHeight: '1.5',
        }}>
          <strong>Security note:</strong> Do not share your forwarding address. Anyone who knows it can add expenses to your account. If compromised, use "Regenerate Address" to get a new one.
        </div>
      </div>

      {/* Forwarded Email History */}
      {address && (
        <div style={styles.section}>
          <div style={styles.chatHeader}>
            <h3 style={{ ...styles.sectionTitle, marginBottom: 0 }}>Forwarded Emails</h3>
            <button
              onClick={handleRefresh}
              disabled={historyLoading}
              style={styles.refreshBtn}
              onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--color-bg-muted)'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--color-bg-base)'}
            >
              {historyLoading ? 'Loading...' : 'Refresh'}
            </button>
          </div>

          {emails.length === 0 && !historyLoading ? (
            <div style={{ textAlign: 'center', padding: 'var(--space-8)', color: 'var(--color-text-tertiary)' }}>
              <p style={{ fontSize: '32px', marginBottom: '8px' }}>📧</p>
              <p style={{ fontSize: 'var(--font-size-sm)' }}>No forwarded emails yet. Forward a receipt to get started!</p>
            </div>
          ) : (
            <div>
              {emails.map((email) => {
                const badge = getStatusBadge(email.status);
                const expense = email.expenses;
                return (
                  <div key={email.id} style={styles.historyItem}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={styles.historySubject}>
                        {email.subject || '(no subject)'}
                      </div>
                      <div style={styles.historyFrom}>
                        From: {email.from_address}
                      </div>
                      <div style={styles.historyTime}>
                        {formatTime(email.created_at)}
                      </div>
                      {email.status === 'failed' && email.error_message && (
                        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-error, #ef4444)', marginTop: '2px' }}>
                          {email.error_message}
                        </div>
                      )}
                      {email.status === 'verification' && email.error_message && (
                        <div style={{ fontSize: 'var(--font-size-xs)', color: '#2563eb', marginTop: '2px' }}>
                          {email.error_message}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                      <span style={{
                        ...styles.statusBadge,
                        backgroundColor: badge.bg,
                        color: badge.color,
                      }}>
                        {badge.label}
                      </span>
                      {expense && (
                        <span style={styles.expenseAmount}>
                          {expense.currency_code} {parseFloat(expense.amount).toFixed(2)}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}

              {historyTotal > emails.length && (
                <button
                  onClick={handleLoadMore}
                  disabled={historyLoading}
                  style={styles.loadMoreBtn}
                >
                  {historyLoading ? 'Loading...' : `Load more (${historyTotal - emails.length} remaining)`}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default EmailForwardingPage;
